import React, { useId } from "react";
import { btn } from "../styles/buttonStyles";

type ScheduleAutomationPanelProps = {
  title: string;
  description: string;
  isEnabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  scheduleDays: string[];
  onDayToggle: (day: string) => void;
  turnOnTimeLabel: string;
  turnOffTimeLabel: string;
  turnOnScheduledTime: string;
  turnOffScheduledTime: string;
  onTurnOnScheduledTimeChange: (value: string) => void;
  onTurnOffScheduledTimeChange: (value: string) => void;
  submitContent: React.ReactNode;
  submitButtonType: "button" | "submit";
  disabled?: boolean;
  className?: string;
};

const DAY_OPTIONS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ScheduleAutomationPanel = ({
  title,
  description,
  isEnabled,
  onEnabledChange,
  scheduleDays,
  onDayToggle,
  turnOnTimeLabel,
  turnOffTimeLabel,
  turnOnScheduledTime,
  turnOffScheduledTime,
  onTurnOnScheduledTimeChange,
  onTurnOffScheduledTimeChange,
  submitContent,
  submitButtonType,
  disabled = false,
  className = "",
}: ScheduleAutomationPanelProps) => {
  // Unique ids per panel, the page can render more than one
  const idPrefix = useId();
  const enabledId = `${idPrefix}-enabled`;
  const turnOnTimeId = `${idPrefix}-turn-on-time`;
  const turnOffTimeId = `${idPrefix}-turn-off-time`;

  // Shared schedule block: real pages and localhost preview use the same markup so they stay in sync.
  return (
    <div className={`workspace-subcard border border-slate-200 bg-white p-3 ${className}`}>
      <h3 className="text-sm font-bold leading-5 text-slate-800">{title}</h3>
      <p className="mt-1 mb-3 text-[11px] leading-4 text-slate-500">{description}</p>

      <div className="mb-3 flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5">
        <label htmlFor={enabledId} className="text-xs font-semibold text-slate-700">Enable schedule</label>
        <label className="inline-flex items-center">
          <input
            id={enabledId}
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            disabled={disabled}
            className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
          />
        </label>
      </div>

      <div className="mb-3">
        <label className="mb-2 block text-xs font-semibold text-slate-700">Schedule Days</label>
        <div className="flex flex-wrap gap-2">
          {DAY_OPTIONS.map((day) => (
            <button
              key={day}
              type="button"
              disabled={disabled || !isEnabled}
              onClick={() => onDayToggle(day)}
              className={`rounded-sm border px-2 py-1 text-xs font-medium transition-colors ${
                scheduleDays.includes(day) && isEnabled
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-gray-300 bg-white text-gray-600 hover:border-blue-400"
              } ${disabled || !isEnabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {day.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={turnOnTimeId} className="mb-2 block text-xs font-semibold text-slate-700">{turnOnTimeLabel}</label>
          <input
            id={turnOnTimeId}
            type="time"
            value={turnOnScheduledTime}
            onChange={(e) => onTurnOnScheduledTimeChange(e.target.value)}
            disabled={disabled || !isEnabled}
            className="w-full rounded-sm border border-gray-300 px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <label htmlFor={turnOffTimeId} className="mb-2 block text-xs font-semibold text-slate-700">{turnOffTimeLabel}</label>
          <input
            id={turnOffTimeId}
            type="time"
            value={turnOffScheduledTime}
            onChange={(e) => onTurnOffScheduledTimeChange(e.target.value)}
            disabled={disabled || !isEnabled}
            className="w-full rounded-sm border border-gray-300 px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      <div className="mt-3 flex justify-start">
        <button type={submitButtonType} disabled={disabled} className={btn.applySmall}>
          {submitContent}
        </button>
      </div>
    </div>
  );
};

export default ScheduleAutomationPanel;
